import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { GraduationCap, Eye, EyeOff, Lock, Mail, Shield, ArrowLeft, Smartphone } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { authApi, consumePostLoginRedirect, consumeLockReason } from '@/services/api'
import { toast } from 'sonner'
import { useLang } from '@/hooks/useLang'

/* ───────────────────────────────────────────────
   Lycée Bilingue de Baleng — LoginPage
   Connexion avec 2FA (TOTP + Email) — Bilingue FR/EN
   Tokens gérés par cookies httpOnly (backend) :
   le store ne reçoit que le user (+ permissions).
   ─────────────────────────────────────────────── */

const t = {
  fr: {
    backHome: "Retour à l'accueil",
    schoolName: 'My Helper School',
    platformDesc: 'Plateforme de gestion scolaire',
    loginTitle: 'Connexion',
    verify2FA: 'Vérification 2FA',
    verifyEmail2FA: 'Vérification par Email',
    emailLabel: 'Email ou Matricule',
    emailPlaceholder: 'franzy2040@email.com',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: '••••••••',
    totpLabel: 'Code 2FA',
    totpPlaceholder: '000000',
    totpHint: "Entrez le code de votre application d'authentification",
    emailOtpLabel: 'Code de vérification',
    emailOtpPlaceholder: '000000',
    emailOtpHint: "Entrez le code envoyé à votre adresse email",
    resendCode: 'Renvoyer le code',
    resendIn: 'Renvoyer dans {s}s',
    loginBtn: 'Se connecter',
    verifyBtn: 'Vérifier',
    backToLogin: 'Retour à la connexion',
    loginSuccess: 'Connexion réussie',
    enter2FA: 'Veuillez entrer votre code 2FA',
    enterEmail2FA: 'Un code de vérification a été envoyé à votre email',
    loginError: 'Erreur de connexion',
    lockedInactivity: "Session verrouillée après 30 min d'inactivité. Vos données sont conservées : reconnectez-vous pour reprendre où vous étiez.",
    lockedExpired: 'Votre session a expiré. Vos données sont conservées : reconnectez-vous pour reprendre où vous étiez.',
    copyright: '© {year} Lycée Bilingue de Baleng',
  },
  en: {
    backHome: 'Back to home',
    schoolName: 'My Helper School',
    platformDesc: 'School Management Platform',
    loginTitle: 'Login',
    verify2FA: '2FA Verification',
    verifyEmail2FA: 'Email Verification',
    emailLabel: 'Email or ID',
    emailPlaceholder: 'your@email.com',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••',
    totpLabel: '2FA Code',
    totpPlaceholder: '000000',
    totpHint: 'Enter the code from your authentication app',
    emailOtpLabel: 'Verification Code',
    emailOtpPlaceholder: '000000',
    emailOtpHint: 'Enter the code sent to your email address',
    resendCode: 'Resend code',
    resendIn: 'Resend in {s}s',
    loginBtn: 'Sign in',
    verifyBtn: 'Verify',
    backToLogin: 'Back to login',
    loginSuccess: 'Login successful',
    enter2FA: 'Please enter your 2FA code',
    enterEmail2FA: 'A verification code has been sent to your email',
    loginError: 'Login error',
    lockedInactivity: 'Session locked after 30 min of inactivity. Your data is preserved: sign back in to resume where you left off.',
    lockedExpired: 'Your session has expired. Your data is preserved: sign back in to resume where you left off.',
    copyright: '© {year} Lycée Bilingue de Baleng',
  }
}

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [emailOtp, setEmailOtp] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [requires2FA, setRequires2FA] = useState(false)
  const [requiresEmail2FA, setRequiresEmail2FA] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [resendTimer, setResendTimer] = useState(0)
  // Motif du verrouillage ('inactivity' | 'expired' | null) — affiché en bannière.
  // Lu depuis sessionStorage, avec repli sur le paramètre URL ?reason=
  const [lockReason] = useState<string | null>(() => {
    const stored = consumeLockReason()
    if (stored) return stored
    try {
      const q = new URLSearchParams(window.location.search)
      return q.get('locked') === '1' ? q.get('reason') : null
    } catch { return null }
  })

  const navigate = useNavigate()
  const location = useLocation()
  const { login, setPermissions } = useAuthStore()
  const { lang } = useLang()
  const txt = t[lang]

  // Compte à rebours pour le renvoi de code
  useEffect(() => {
    if (resendTimer <= 0) return
    const interval = setInterval(() => {
      setResendTimer(prev => prev - 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [resendTimer])

  // ═══ Login réussi : remplit le store et redirige ═══
  const applyLogin = (response: any) => {
    login(response.user)
    if (response.permissions) {
      setPermissions(
        response.permissions,
        response.role,
        response.role_name,
        response.is_admin,
        response.is_super_admin
      )
    }
    toast.success(txt.loginSuccess)
    // 1) Page sauvegardée lors du verrouillage (api.ts) — restaure aussi les formulaires
    // 2) Redirection React Router classique (location.state.from)
    // 3) Dashboard par défaut
    const redirect = consumePostLoginRedirect()
    const from = redirect || location.state?.from || '/dashboard'
    navigate(from, { replace: true })
  }

  const handleResendEmailOtp = async () => {
    if (resendTimer > 0) return
    setIsLoading(true)
    try {
      const res = await authApi.login(email, password) as any
      if (res.requires_email_2fa) {
        toast.success(txt.enterEmail2FA)
        setResendTimer(60)
      }
    } catch (err: any) {
      toast.error(err.message || txt.loginError)
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // ═══ ÉTAPE 1 : Email 2FA ═══
      if (requiresEmail2FA) {
        const response = await authApi.loginWithEmail2FA(email, password, emailOtp) as any
        applyLogin(response)
        return
      }

      // ═══ ÉTAPE 2 : TOTP 2FA ═══
      if (requires2FA) {
        const response = await authApi.loginWith2FA(email, password, totpCode) as any
        applyLogin(response)
        return
      }

      // ═══ ÉTAPE 3 : Login initial ═══
      const response = await authApi.login(email, password) as any

      if (response.requires_email_2fa) {
        setRequiresEmail2FA(true)
        toast.info(txt.enterEmail2FA)
        setResendTimer(60)
        setIsLoading(false)
        return
      }

      if (response.requires_2fa) {
        setRequires2FA(true)
        toast.info(txt.enter2FA)
        setIsLoading(false)
        return
      }

      applyLogin(response)

    } catch (err: any) {
      toast.error(err.message || txt.loginError)
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setRequires2FA(false)
    setRequiresEmail2FA(false)
    setTotpCode('')
    setEmailOtp('')
    setResendTimer(0)
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">

        {/* ─── RETOUR À L'ACCUEIL ─── */}
        <div className="mb-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-blue-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {txt.backHome}
          </Link>
        </div>

        {/* Logo cliquable */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-blue-900 mb-4 hover:bg-blue-800 transition-colors cursor-pointer">
              <GraduationCap className="h-8 w-8 text-yellow-500" />
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{txt.schoolName}</h1>
          <p className="text-gray-500">{txt.platformDesc}</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-center mb-6">
              {requiresEmail2FA ? txt.verifyEmail2FA : requires2FA ? txt.verify2FA : txt.loginTitle}
            </h2>

            {/* Bannière après verrouillage de session : rassure l'utilisateur
                que ses données sont conservées et qu'il reprendra où il en était */}
            {lockReason && (
              <div className="mb-6 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                <Shield className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  {lockReason === 'inactivity' ? txt.lockedInactivity : txt.lockedExpired}
                </p>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              {/* Champs Email + Password (visibles au début) */}
              {!requires2FA && !requiresEmail2FA && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{txt.emailLabel}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={txt.emailPlaceholder}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{txt.passwordLabel}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={txt.passwordPlaceholder}
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Champ TOTP 2FA */}
              {requires2FA && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{txt.totpLabel}</label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={txt.totpPlaceholder}
                      maxLength={6}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {txt.totpHint}
                  </p>
                </div>
              )}

              {/* Champ Email OTP 2FA */}
              {requiresEmail2FA && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{txt.emailOtpLabel}</label>
                  <div className="relative">
                    <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={txt.emailOtpPlaceholder}
                      maxLength={6}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {txt.emailOtpHint}
                  </p>
                  <button
                    type="button"
                    onClick={handleResendEmailOtp}
                    disabled={resendTimer > 0 || isLoading}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {resendTimer > 0 
                      ? txt.resendIn.replace('{s}', String(resendTimer)) 
                      : txt.resendCode}
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 bg-blue-900 text-white rounded-lg font-medium hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : requiresEmail2FA || requires2FA ? (
                  txt.verifyBtn
                ) : (
                  txt.loginBtn
                )}
              </button>

              {(requires2FA || requiresEmail2FA) && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  disabled={isLoading}
                >
                  {txt.backToLogin}
                </button>
              )}
            </form>
          </div>
        </div>

        <p className="text-center mt-6 text-sm text-gray-500">
          {txt.copyright.replace('{year}', String(new Date().getFullYear()))}
        </p>
      </div>
    </div>
  )
}